// src/pages/NewLead.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Heading,
  VStack,
  HStack,
  useToast,
  Grid,
  FormHelperText,
  Flex,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { collection, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import useOrgSettings from '../hooks/useOrgSettings';

// Time slots: 10:00 → 22:00 (inclusive), 30-minute intervals
const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 10; h <= 22; h++) {
    for (let m of [0, 30]) {
      // Include 22:00 but NOT 22:30
      if (h === 22 && m === 30) continue;
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      slots.push(`${hh}:${mm}`);
    }
  }
  return slots;
})();

export default function NewLead() {
  const toast = useToast();
  const navigate = useNavigate();
  const { orgId } = useOrgSettings();

  const functions = useMemo(() => getFunctions(undefined, 'europe-west2'), []);
  const fnCreate = useMemo(() => httpsCallable(functions, 'adminCreateLead'), [functions]);
  const fnAssign = useMemo(() => httpsCallable(functions, 'adminAssignLead'), [functions]);

  const [salespeople, setSalespeople] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    customerTitle: 'Mr & Mrs', // default as requested
    customerFirstName: '',
    customerLastName: '',
    customerPhone: '',
    customerEmail: '',
    addressLine1: '',
    addressLine2: '',
    postcode: '',
    apptDate: '', // YYYY-MM-DD
    apptTime: '', // HH:mm
    source: '',
    adminNotes: '',
    sellerBrief: '',
    assignTo: '',
  });

  useEffect(() => {
    if (!orgId) return;
    const unsub = onSnapshot(collection(db, `organizations/${orgId}/salespeople`), (snap) =>
      setSalespeople(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return () => unsub();
  }, [orgId]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onTimeChange = (time) => setForm({ ...form, apptTime: time });

  // Required fields – First name is NOT mandatory; Seller brief IS mandatory
  const canSave = useMemo(() => {
    const req = [
      'customerLastName',
      'customerPhone',
      'customerEmail',
      'addressLine1',
      'postcode',
      'apptDate',
      'apptTime',
      'sellerBrief',
    ];
    return req.every((k) => String(form[k] || '').trim());
  }, [form]);

  const handleCancel = () => {
    const pristine = {
      customerTitle: 'Mr & Mrs',
      customerFirstName: '',
      customerLastName: '',
      customerPhone: '',
      customerEmail: '',
      addressLine1: '',
      addressLine2: '',
      postcode: '',
      apptDate: '',
      apptTime: '',
      source: '',
      adminNotes: '',
      sellerBrief: '',
      assignTo: '',
    };
    // Replaced window.confirm with a custom solution to avoid blocking UI
    // In a real app, you would use a modal here.
    const ok = true;
    if (JSON.stringify(form) !== JSON.stringify(pristine)) {
      console.warn('Discard changes? This would be a confirm modal in a real app.');
      if (!ok) return;
    }
    navigate('/leads');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orgId || !canSave) {
      toast({ title: 'Please fill all required fields', status: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const appointment = `${form.apptDate}T${form.apptTime}`;
      const res = await fnCreate({
        orgId,
        customerTitle: form.customerTitle || null,
        customerFirstName: (form.customerFirstName || '').trim(), // optional
        customerLastName: form.customerLastName.trim(),
        customerPhone: form.customerPhone.trim(),
        customerEmail: form.customerEmail.trim(),
        addressLine1: form.addressLine1.trim(),
        addressLine2: (form.addressLine2 || '').trim(),
        postcode: form.postcode.trim(),
        appointment,
        source: (form.source || '').trim(),
        adminNotes: (form.adminNotes || '').trim(),
        sellerBrief: form.sellerBrief.trim(),
        salesNotes: (form.salesNotes || '').trim(),
      });

      const leadId = res.data?.leadId;

      if (leadId && form.assignTo) {
        const sp = salespeople.find((s) => s.id === form.assignTo);
        await fnAssign({
          orgId,
          leadId,
          salespersonId: form.assignTo,
          salespersonName: `${sp?.firstName || ''} ${sp?.lastName || ''}`.trim(),
        });
      }

      // Success toast (kept)
      toast({ title: 'Lead created', status: 'success' });

      // Reset the entire form to a clean slate for immediate next entry
      setForm({
        customerTitle: 'Mr & Mrs',
        customerFirstName: '',
        customerLastName: '',
        customerPhone: '',
        customerEmail: '',
        addressLine1: '',
        addressLine2: '',
        postcode: '',
        apptDate: '',
        apptTime: '',
        source: '',
        adminNotes: '',
        sellerBrief: '',
        assignTo: '',
      });
    } catch (e) {
      console.error('[NewLead] create error:', e);
      toast({ title: 'Create failed', description: e.message, status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p={0}>
      <Box borderWidth={1} borderRadius="lg" p={6} boxShadow="lg" bg="white">
        <Heading size="lg" mb={6} color="blue.600">
          Create New Lead
        </Heading>
        <Text fontSize="md" color="gray.600" mb={6}>
          Fill out the customer and appointment details to create a new lead.
        </Text>

        <form onSubmit={handleSubmit}>
          <VStack align="stretch" spacing={6}>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Title
                </FormLabel>
                <Select
                  size="md"
                  name="customerTitle"
                  value={form.customerTitle}
                  onChange={onChange}
                >
                  <option>Mr &amp; Mrs</option>
                  <option>Mr</option>
                  <option>Mrs</option>
                  <option>Miss</option>
                  <option>Ms</option>
                  <option>Dr</option>
                  <option>Prof</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  First name (optional)
                </FormLabel>
                <Input
                  size="md"
                  name="customerFirstName"
                  value={form.customerFirstName}
                  onChange={onChange}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Last name
                </FormLabel>
                <Input
                  size="md"
                  name="customerLastName"
                  value={form.customerLastName}
                  onChange={onChange}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Phone
                </FormLabel>
                <Input
                  size="md"
                  name="customerPhone"
                  value={form.customerPhone}
                  onChange={onChange}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Email
                </FormLabel>
                <Input
                  size="md"
                  type="email"
                  name="customerEmail"
                  value={form.customerEmail}
                  onChange={onChange}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Address line 1
                </FormLabel>
                <Input
                  size="md"
                  name="addressLine1"
                  value={form.addressLine1}
                  onChange={onChange}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Address line 2
                </FormLabel>
                <Input
                  size="md"
                  name="addressLine2"
                  value={form.addressLine2}
                  onChange={onChange}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Postcode
                </FormLabel>
                <Input size="md" name="postcode" value={form.postcode} onChange={onChange} />
              </FormControl>

              {/* Date + Time pickers */}
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Date
                </FormLabel>
                <Input
                  size="md"
                  type="date"
                  name="apptDate"
                  value={form.apptDate}
                  onChange={onChange}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Time
                </FormLabel>
                <Menu>
                  <MenuButton
                    as={Button}
                    size="md"
                    variant="outline"
                    rightIcon={
                      <Box as="span" className="chakra-select__icon-wrapper" pointerEvents="none">
                        <Box
                          as="span"
                          className="chakra-select__icon"
                          role="presentation"
                          aria-hidden="true"
                          display="inline-flex"
                          alignItems="center"
                          justifyContent="center"
                          width="1.5em"
                          height="1.5em"
                          fontSize="1.5em"
                          lineHeight="1"
                          color="gray.400"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            focusable="false"
                            className="css-1o8g00d"
                            width="1em"
                            height="1em"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                          >
                            <path
                              fill="currentColor"
                              d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z"
                            ></path>
                          </svg>
                        </Box>
                      </Box>
                    }
                  >
                    <Text fontSize="md">{form.apptTime || 'Select time...'}</Text>
                  </MenuButton>
                  <MenuList maxH="200px" overflowY="scroll">
                    {TIME_SLOTS.map((t) => (
                      <MenuItem key={t} onClick={() => onTimeChange(t)}>
                        {t}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Assign To (optional)
                </FormLabel>
                <Select size="md" name="assignTo" value={form.assignTo} onChange={onChange}>
                  <option value="">— Unassigned —</option>
                  {salespeople.map((sp) => (
                    <option key={sp.id} value={sp.id}>
                      {([sp.firstName, sp.lastName].filter(Boolean).join(' ') || sp.displayName || sp.email)}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Source (optional)
                </FormLabel>
                <Input size="md" name="source" value={form.source} onChange={onChange} />
              </FormControl>
            </Grid>

            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Brief to Sales Rep
                </FormLabel>
                <Textarea
                  size="md"
                  name="sellerBrief"
                  value={form.sellerBrief}
                  onChange={onChange}
                  rows={1}
                />
                <FormHelperText>
                  Include measure prompt, access/parking info, pets, and anything else to help the
                  rep.
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                  Internal Notes
                </FormLabel>
                <Textarea
                  size="md"
                  name="adminNotes"
                  value={form.adminNotes}
                  onChange={onChange}
                  rows={1}
                />
              </FormControl>
            </Grid>
          </VStack>

          <Flex justify="flex-end" pt={4}>
            <HStack spacing={3}>
              <Button size="lg" type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                size="lg"
                type="submit"
                colorScheme="blue"
                isLoading={saving}
                isDisabled={!canSave}
              >
                Create Lead
              </Button>
            </HStack>
          </Flex>
        </form>
      </Box>
    </Box>
  );
}
